import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { saveFilters } from "@/lib/filterCookies";
import { Bell, BellOff, Trash2, Pencil, Search, Share2, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import SportIcon from "@/components/SportIcon";
import AlertEditModal from "@/components/AlertEditModal";

/**
 * AlertsPanel — the shared saved-search list (chips + Edit/Pause/Delete + edit modal).
 * Rendered on both the standalone /alerts page and the Account Management page.
 * Both read/write the same /saved-searches API, so edits stay in sync.
 *
 * Props:
 *   title  optional compact section header (Account uses it; the /alerts page omits it
 *          because it already has a big page heading).
 */
export default function AlertsPanel({ title, token = "" }) {
    const [alerts, setAlerts] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [editing, setEditing] = useState(null);
    const navigate = useNavigate();
    const qs = token ? `?token=${encodeURIComponent(token)}` : "";

    // Load a saved alert's exact filters back onto the search page.
    const applyToSearch = (a) => {
        saveFilters({
            search: a.search || "",
            sport: (a.sports && a.sports[0]) || "Basketball",
            domains: a.domains && a.domains.length ? a.domains : ["team", "trainer", "event"],
            states: a.states || [],
            ages: a.ages || [],
            grades: a.grades || [],
            genders: a.genders || [],
            circuits: a.circuits || [],
            near_zip: a.near_zip || "",
            radius_miles: a.radius_miles || "",
            events_when: "upcoming",
            view: "map",
        });
        toast.success(`Loaded "${a.label}" into search`);
        navigate("/");
    };

    // Build a shareable link that opens the search pre-filtered with this alert's filters.
    const shareUrl = (a) => {
        const p = new URLSearchParams();
        if (a.search) p.set("q", a.search);
        if (a.sports && a.sports[0]) p.set("sport", a.sports[0]);
        if (a.domains && a.domains.length) p.set("domains", a.domains.join(","));
        if (a.states && a.states.length) p.set("states", a.states.join(","));
        if (a.ages && a.ages.length) p.set("ages", a.ages.join(","));
        if (a.grades && a.grades.length) p.set("grades", a.grades.join(","));
        if (a.genders && a.genders.length) p.set("genders", a.genders.join(","));
        if (a.circuits && a.circuits.length) p.set("circuits", a.circuits.join(","));
        if (a.near_zip) p.set("zip", a.near_zip);
        if (a.radius_miles) p.set("radius", a.radius_miles);
        return `${window.location.origin}/?${p.toString()}`;
    };

    const copyShare = async (a) => {
        const url = shareUrl(a);
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Share link copied to clipboard");
        } catch {
            // Fallback for browsers that block clipboard access.
            window.prompt("Copy this search link:", url);
        }
    };

    useEffect(() => {
        setFetching(true);
        api.get(`/saved-searches${qs}`)
            .then((r) => setAlerts(r.data))
            .catch(() => setAlerts([]))
            .finally(() => setFetching(false));
    }, [qs]);

    const toggleEmail = async (a) => {
        try {
            const { data } = await api.put(`/saved-searches/${a.id}${qs}`, { email_enabled: !a.email_enabled });
            setAlerts((prev) => prev.map((x) => (x.id === a.id ? data : x)));
            toast.success(data.email_enabled ? "Email alerts enabled" : "Email alerts paused");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to update alert");
        }
    };

    const remove = async (a) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete alert "${a.label}"?`)) return;
        try {
            await api.delete(`/saved-searches/${a.id}${qs}`);
            setAlerts((prev) => prev.filter((x) => x.id !== a.id));
            toast.success("Alert deleted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to delete alert");
        }
    };

    return (
        <div data-testid="alerts-panel">
            {title ? (
                <div className="flex items-center gap-2 mb-3">
                    <Bell className="h-4 w-4 text-[#ff4500]" />
                    <div className="text-xs uppercase tracking-[0.2em] text-[#a0a0a0]">{title}</div>
                    <div className="text-[10px] text-[#a0a0a0]">— We email you when new matches are added. Save one from the <Link to="/" className="text-[#ff4500] hover:underline">home page</Link>.</div>
                </div>
            ) : null}

            {fetching ? (
                <div className="text-sm text-[#a0a0a0] py-6">Loading alerts…</div>
            ) : alerts.length === 0 ? (
                <div className="border border-[#2a2a2a] bg-[#141414] py-10 text-center" data-testid="alerts-empty">
                    <div className="font-display text-2xl mb-1">No saved searches yet</div>
                    <div className="text-sm text-[#a0a0a0]">
                        Pick filters on the <Link to="/" className="text-[#ff4500] hover:underline">home page</Link> and click <strong>Save Search</strong>.
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    {alerts.map((a) => {
                        const segs = [
                            { key: "sport", label: "Sport", values: a.sports, withSportIcon: true, fallback: "All" },
                            { key: "listings", label: "Listings", values: a.domains, fallback: "All" },
                            { key: "states", label: "States", values: a.states, fallback: "All" },
                            { key: "ages", label: "Ages", values: a.ages, fallback: "All" },
                            { key: "grades", label: "Grades", values: a.grades, fallback: "All" },
                            { key: "genders", label: "Genders", values: mapGenders(a.genders), fallback: "All" },
                            { key: "circuits", label: "Circuits", values: a.circuits, fallback: "All" },
                            ...(a.event_types && a.event_types.length ? [{ key: "type", label: "Type", values: a.event_types }] : []),
                            { key: "near", label: "Near", values: a.near_zip ? [a.near_zip] : null, fallback: "Any ZIP" },
                            { key: "radius", label: "Radius", values: a.near_zip && a.radius_miles ? [`${a.radius_miles} mi`] : null, fallback: a.near_zip ? "Unlimited" : "Any" },
                            { key: "keyword", label: "Keyword", values: a.search ? [`"${a.search}"`] : null, fallback: "Any" },
                        ];
                        const iconBtn = "h-8 w-8 inline-flex items-center justify-center transition-colors shrink-0";
                        return (
                            <div key={a.id} data-testid={`alert-${a.id}`} className="border border-[#2a2a2a] bg-[#141414] px-4 py-3">
                                {/* Row 1 — bell + title get the whole row; title truncates with … past the edge */}
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className={`h-8 w-8 border flex items-center justify-center shrink-0 ${a.email_enabled ? "border-[#ff4500] text-[#ff4500]" : "border-[#3a3a3a] text-[#666]"}`}>
                                        {a.email_enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                                    </div>
                                    <div className={`font-display text-2xl tracking-wide truncate min-w-0 ${a.email_enabled ? "" : "text-[#8a8a8a]"}`} title={a.label}>{a.label}</div>
                                    {!a.email_enabled && (
                                        <span className="text-[9px] uppercase tracking-[0.15em] text-[#777] border border-[#3a3a3a] px-1.5 py-0.5 shrink-0">Paused</span>
                                    )}
                                </div>

                                {/* Row 2 — icon-only actions; label shows on hover (desktop) / long-press (mobile) */}
                                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                                    <button data-testid={`alert-apply-${a.id}`} onClick={() => applyToSearch(a)} title="Load" aria-label="Load" className={`${iconBtn} border border-[#ff4500] text-[#ff4500] hover:bg-[#ff4500]/10`}>
                                        <Search className="h-4 w-4" />
                                    </button>
                                    <button data-testid={`alert-share-${a.id}`} onClick={() => copyShare(a)} title="Share" aria-label="Share" className={`${iconBtn} border border-[#2a2a2a] text-[#cfcfcf] hover:border-[#ff4500] hover:text-[#ff4500]`}>
                                        <Share2 className="h-4 w-4" />
                                    </button>
                                    <button data-testid={`alert-edit-${a.id}`} onClick={() => setEditing(a)} title="Edit" aria-label="Edit" className={`${iconBtn} border border-[#2a2a2a] text-[#cfcfcf] hover:border-[#ff4500] hover:text-[#ff4500]`}>
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                    <button data-testid={`alert-toggle-${a.id}`} onClick={() => toggleEmail(a)} title={a.email_enabled ? "Pause" : "Enable"} aria-label={a.email_enabled ? "Pause" : "Enable"} className={`${iconBtn} border border-[#2a2a2a] text-[#cfcfcf] hover:border-[#ff4500] hover:text-[#ff4500]`}>
                                        {a.email_enabled ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                    </button>
                                    <button data-testid={`alert-delete-${a.id}`} onClick={() => remove(a)} title="Delete" aria-label="Delete" className={`${iconBtn} border border-[#2a2a2a] text-[#a0a0a0] hover:border-red-500 hover:text-red-500`}>
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>

                                {/* Row 3 — every filter as tight "Label: value" dot-separated text */}
                                <div className="mt-2 flex flex-wrap items-center text-[12px] leading-relaxed text-[#a0a0a0]">
                                    {segs.map((s, i) => (
                                        <span key={s.key} className="inline-flex items-center">
                                            {i > 0 && <span className="text-[#4a4a4a] px-1.5">·</span>}
                                            <FilterText label={s.label} values={s.values} withSportIcon={s.withSportIcon} fallback={s.fallback} />
                                        </span>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {editing && (
                <AlertEditModal
                    alert={editing}
                    token={token}
                    onClose={() => setEditing(null)}
                    onSaved={(updated) => {
                        setAlerts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
}

function mapGenders(genders) {
    if (!genders || !genders.length) return null;
    return genders.map((g) => (g === "M" ? "Boys" : g === "F" ? "Girls" : g));
}

function FilterText({ label, values, withSportIcon, fallback }) {
    const empty = !values || !values.length;
    if (empty && !fallback) return null;
    return (
        <span className="whitespace-nowrap">
            <span className="text-[#6a6a6a]">{label}:</span>{" "}
            {empty ? (
                <span className="text-[#6a6a6a]">{fallback}</span>
            ) : withSportIcon ? (
                <span className="inline-flex items-center gap-1 align-middle text-[#d7d7d7]">
                    <SportIcon sport={values[0]} className="h-3 w-3" />
                    {values.join(", ")}
                </span>
            ) : (
                <span className="text-[#d7d7d7]">{values.join(", ")}</span>
            )}
        </span>
    );
}
